// src/pages/bluebi/PainelFormModalContent.tsx
// Cadastro de painel do Blue B.I. (issue #42): nome, link do relatório e quem vê.
//
// O componente é montado apenas enquanto o modal está aberto (o `Modal` devolve `null`
// quando fechado), então fechar pelo "x" — ou salvar — desmonta e o formulário nasce
// limpo na próxima abertura. Não há botão de desistir: o "x" é o gesto de desistência.
//
// ⚠️ O `link` digitado é uma URL "Publish to web" anônima e permanente: não vai para
// `logger.*`, não aparece em mensagem de erro e não entra em URL de navegação.
import React, { useEffect, useMemo, useState } from 'react';
import SaveButton from '../../components/common/SaveButton';
import MultiSelectList from '../../components/common/MultiSelectList';
import { apiGet } from '../../services/api';
import { logger } from '@/utils/logger';
import { Pessoa } from '../../types/usuarios/Pessoa';
import { PainelGestao, ROTULO_MAX_LENGTH, slugDoRotulo, validarLinkPainel } from '../../types/bluebi/Painel';
import './BlueBi.css';

interface UsuarioOpcao {
  id: number;
  nome: string;
  userName: string;
}

interface PainelFormModalContentProps {
  theme: 'dark' | 'light';
  isLoading: boolean;
  /** Presente = edição; ausente = cadastro. */
  painel?: PainelGestao;
  /** Rótulos dos OUTROS painéis — usados para recusar um nome que colidiria na URL. */
  rotulosExistentes: string[];
  onSave: (dados: { rotulo: string; link: string; usuarioIds: number[] }) => Promise<boolean>;
  /** Só em edição: tira o painel de circulação ou o devolve a ela. */
  onToggleAtivo?: (ativar: boolean) => Promise<boolean>;
}

const PAGE_SIZE = 50;

const PainelFormModalContent: React.FC<PainelFormModalContentProps> = ({
  theme,
  isLoading,
  painel,
  rotulosExistentes,
  onSave,
  onToggleAtivo,
}) => {
  const emEdicao = painel !== undefined;
  const [rotulo, setRotulo] = useState(painel?.rotulo ?? '');
  const [link, setLink] = useState(painel?.link ?? '');
  const [usuarios, setUsuarios] = useState<UsuarioOpcao[]>([]);
  const [selecionados, setSelecionados] = useState<number[]>([]);
  const [carregandoUsuarios, setCarregandoUsuarios] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  // Em edição, os vínculos atuais vêm do backend (só ids — o nome sai da listagem acima).
  //
  // O salvamento sincroniza os acessos por DIFF contra o que o backend tem, então uma falha
  // aqui não pode passar batida: com a lista vazia por erro, salvar emitiria um DELETE por
  // vínculo existente e revogaria todos os acessos do painel. Enquanto esta carga não tiver
  // sucesso, `vinculosCarregados` fica falso e o salvamento fica bloqueado.
  const [vinculosCarregados, setVinculosCarregados] = useState(painel === undefined);

  useEffect(() => {
    if (!painel) return;
    let cancelado = false;
    apiGet(`/v1/bluebi/gestao/paineis/${painel.id}/usuarios`)
      .then((res) => {
        if (cancelado) return;
        const ids: number[] = (res.data ?? []).map((v: { usuarioId: number }) => v.usuarioId);
        setSelecionados(ids);
        setVinculosCarregados(true);
      })
      .catch((err) => {
        logger.error('Falha ao carregar vínculos:', err instanceof Error ? err.message : String(err));
        if (!cancelado) {
          setErro(
            'Não foi possível carregar quem tem acesso a este painel. Feche e abra novamente — ' +
              'salvar agora removeria os acessos existentes.'
          );
        }
      });
    return () => {
      cancelado = true;
    };
  }, [painel]);

  // Percorre as páginas de `/v1/pessoas?ativo=true` até esgotar. A rota não tem busca
  // server-side (aceita só ativo/dados-pessoais/page/size), então a lista é carregada
  // inteira e a escolha é feita no dropdown. Se a base de usuários crescer a ponto de
  // isso pesar, o caminho é o backend ganhar busca — não paginar a seleção na mão.
  useEffect(() => {
    let cancelado = false;
    const carregar = async () => {
      setCarregandoUsuarios(true);
      try {
        const acumulado: UsuarioOpcao[] = [];
        for (let page = 0; ; page++) {
          const res = await apiGet(`/v1/pessoas?ativo=true&page=${page}&size=${PAGE_SIZE}`);
          const conteudo: Pessoa[] = res.data?.content ?? [];
          acumulado.push(
            ...conteudo
              .filter((p) => p.id !== undefined && p.usuario?.userName)
              .map((p) => ({
                id: p.id as number,
                nome: p.nome,
                userName: p.usuario?.userName as string,
              }))
          );
          const total: number = res.data?.page?.totalElements ?? acumulado.length;
          if (conteudo.length === 0 || acumulado.length >= total) break;
        }
        if (!cancelado) setUsuarios(acumulado);
      } catch (err) {
        logger.error('Falha ao carregar usuários:', err instanceof Error ? err.message : String(err));
        if (!cancelado) setErro('Não foi possível carregar a lista de usuários.');
      } finally {
        if (!cancelado) setCarregandoUsuarios(false);
      }
    };
    carregar();
    return () => {
      cancelado = true;
    };
  }, []);

  const alternarUsuario = (id: number) => {
    setSelecionados((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  // Salvar só existe com os três: nome, link e ao menos um usuário. Um painel sem
  // ninguém vinculado não seria visto por nenhum usuário comum. Em edição, exige também
  // que os vínculos atuais tenham sido lidos — ver o comentário da carga acima.
  const podeSalvar = useMemo(
    () =>
      rotulo.trim().length > 0 &&
      link.trim().length > 0 &&
      selecionados.length > 0 &&
      vinculosCarregados &&
      !isLoading,
    [rotulo, link, selecionados, vinculosCarregados, isLoading]
  );

  const handleSave = async () => {
    setErro(null);

    const erroLink = validarLinkPainel(link);
    if (erroLink) {
      setErro(erroLink);
      return;
    }

    // O rótulo vira o slug da URL do painel. O backend aceita rótulos repetidos, então
    // dois painéis poderiam disputar o mesmo `/bluebi/<slug>` — recusar aqui evita criar
    // o estado ambíguo, em vez de ter que desempatá-lo depois.
    const slugNovo = slugDoRotulo(rotulo);
    if (!slugNovo) {
      setErro('Informe um nome com ao menos uma letra ou número.');
      return;
    }
    if (rotulosExistentes.some((existente) => slugDoRotulo(existente) === slugNovo)) {
      setErro('Já existe um painel com esse nome.');
      return;
    }

    await onSave({ rotulo: rotulo.trim(), link: link.trim(), usuarioIds: selecionados });
  };

  return (
    <div className={`form-content bluebi-form ${theme}`}>
      <div className="facelet-h3">
        <h3>{emEdicao ? 'Visualizando: Editar painel' : 'Visualizando: Novo painel'}</h3>
      </div>
      <hr />
      <div className="form-grid">
        <label>
          Nome:
          <input
            type="text"
            value={rotulo}
            // Minúsculo é a convenção de nome de painel definida para o módulo (ex.: vendas, dre).
            onChange={(e) => setRotulo(e.target.value.toLowerCase())}
            maxLength={ROTULO_MAX_LENGTH}
            placeholder="vendas"
            disabled={isLoading}
          />
        </label>
        <label>
          iframe:
          <input
            type="text"
            value={link}
            onChange={(e) => setLink(e.target.value)}
            placeholder={`https://app.powerbi.com/view?r=...`}
            disabled={isLoading}
          />
        </label>
        {carregandoUsuarios ? (
          <p>Carregando usuários...</p>
        ) : (
          <MultiSelectList
            items={usuarios}
            selectedIds={selecionados}
            handleChange={alternarUsuario}
            theme={theme}
            label="Usuários com acesso"
            displayFields={[
              { key: 'nome', label: 'Nome' },
              { key: 'userName', label: 'Usuário' },
            ]}
            disabled={isLoading}
          />
        )}
      </div>
      {erro && <span className="error-message">{erro}</span>}
      <p className="bluebi-aviso-modal">
        O link do relatório é público e anônimo: quem o tiver abre sem login, e a aplicação não
        consegue revogá-lo. Retirar o painel do ar depois não fecha o acesso de quem já o copiou —
        isso só se faz no próprio Power B.I.
      </p>
      {/* Sem botão de desistir: o "x" do Modal é o gesto de desistência, e desmontar o
          componente já devolve o formulário vazio na próxima abertura. */}
      <div className="form-actions">
        {emEdicao && onToggleAtivo && (
          <button
            type="button"
            className="bluebi-toggle-ativo"
            onClick={() => onToggleAtivo(painel.ativo === false)}
            disabled={isLoading}
          >
            {painel.ativo === false ? 'Reativar painel' : 'Desativar painel'}
          </button>
        )}
        <SaveButton isLoading={isLoading} onClick={handleSave} disabled={!podeSalvar} />
      </div>
    </div>
  );
};

export default PainelFormModalContent;
